import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { AlertOctagon, Mail, Phone, ArrowUpRight, Shield } from "lucide-react";
import MarketingShell from "../components/marketing/MarketingShell";

export default function Refund() {
  useEffect(() => {
    document.title = "Refund Policy — EcomCatalog AI";
  }, []);

  return (
    <MarketingShell>
      <section className="pt-16 sm:pt-24 pb-12">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-400 font-bold">
            Legal · Last updated 24 June 2026
          </div>
          <h1 className="font-mkt-display text-5xl sm:text-6xl font-semibold tracking-tighter mt-4 leading-[1.02]">
            Refund <span className="mkt-gradient-text-vivid">Policy.</span>
          </h1>
          <p className="text-lg text-slate-400 mt-6 leading-relaxed">
            EcomCatalog AI is a digital, usage-based SaaS product. The clauses below
            describe how subscription charges, listing-credit packs and add-on services
            are handled. By making a payment, you accept these terms in full.
          </p>
        </div>
      </section>

      {/* The non-refundable callout — front and centre */}
      <section className="pb-12">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div
            className="rounded-3xl p-7 sm:p-9 border"
            style={{
              borderColor: "rgba(244,63,94,0.35)",
              background: "linear-gradient(180deg, rgba(244,63,94,0.10) 0%, rgba(244,63,94,0.04) 100%)",
              boxShadow: "0 30px 80px -25px rgba(244,63,94,0.45)",
            }}
            data-testid="refund-policy-callout"
          >
            <div className="flex items-start gap-4">
              <div
                className="shrink-0 w-12 h-12 grid place-items-center rounded-2xl"
                style={{ background: "rgba(244,63,94,0.18)", color: "#f43f5e" }}
              >
                <AlertOctagon size={22} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-rose-300">
                  Important — please read
                </div>
                <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1.5"
                    style={{ fontFamily: "'Outfit', sans-serif" }}>
                  All payments to EcomCatalog AI are <span className="text-rose-400">non-refundable</span> —
                  under any condition.
                </h2>
                <p className="text-sm text-slate-300 mt-3 leading-relaxed">
                  Once a transaction is verified and your account is credited, the money paid to
                  EcomCatalog AI is final. We do not issue full or partial refunds, credits in lieu
                  of refunds, or pro-rated cancellations — irrespective of whether the listing-credit
                  pack has been fully consumed, partially used, or left untouched.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Clauses */}
      <section className="pb-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 space-y-10">
          {CLAUSES.map((c) => (
            <article key={c.title} className="text-slate-300" data-testid={`clause-${c.id}`}>
              <h3 className="font-mkt-display text-2xl font-semibold tracking-tight text-white">
                {c.title}
              </h3>
              <div className="mt-3 space-y-2 leading-relaxed text-[15px] text-slate-400">
                {c.body.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              {c.bullets && (
                <ul className="mt-3 space-y-2 list-disc pl-5 text-[15px] text-slate-400">
                  {c.bullets.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              )}
            </article>
          ))}
        </div>
      </section>

      {/* Help section */}
      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="rounded-3xl p-8 sm:p-10 mkt-glass-strong text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mkt-glass text-[10px] uppercase tracking-[0.25em] text-indigo-300 font-bold">
              <Shield size={11} /> Questions?
            </div>
            <h3 className="font-mkt-display text-3xl font-semibold tracking-tighter mt-4">
              Talk to us before paying.
            </h3>
            <p className="text-sm text-slate-400 mt-3 max-w-xl mx-auto leading-relaxed">
              Reach out for a no-commitment walkthrough so you know exactly what you&apos;re
              buying. We&apos;d rather have an aligned customer than a confused refund request.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <a
                href="tel:+919464645491"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-bold text-white"
                style={{ background: "linear-gradient(135deg, #16a34a 0%, #059669 100%)" }}
                data-testid="refund-call-cta"
              >
                <Phone size={14} /> +91 94646 45491
              </a>
              <a
                href="mailto:business@brandborn.in"
                className="mkt-btn-ghost"
                data-testid="refund-email-cta"
              >
                <Mail size={14} /> business@brandborn.in
              </a>
              <Link to="/pricing#pricing" className="mkt-btn-gradient" data-testid="refund-pricing-cta">
                See Plans <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

const CLAUSES = [
  {
    id: "scope",
    title: "1. What this policy covers",
    body: [
      "This refund policy applies to every product, plan, listing-credit pack, AI service and add-on sold under the EcomCatalog AI / BrandBorn AI brand — including the Starter (₹2,499 / 100 listings), Pro (₹4,999 / 500 listings) and Business (₹8,999 / 1000 listings) plans, custom credit top-ups, and any one-time onboarding service.",
      "All plan prices are inclusive of taxes from our side — we do NOT add GST on top of the listed price. Equally, the invoice we issue does not contain a GST component. If you require a GST-input-credit-eligible tax invoice, contact business@brandborn.in BEFORE making payment.",
      "Payments are received via UPI / bank transfer based on the QR you scan on our Pricing page. Once your transaction is verified and credits are added to your account, the policy in clause 2 kicks in.",
    ],
  },
  {
    id: "no-refund",
    title: "2. No refunds — under any condition",
    body: [
      "All payments are strictly non-refundable. This includes, but is not limited to, the following common requests:",
    ],
    bullets: [
      "Change of mind after purchase or buyer's remorse.",
      "Unused listing credits at the end of any month, quarter or year.",
      "Partial usage — e.g. only 12 of 100 listings generated.",
      "Account suspension or cancellation triggered by violation of our Terms of Service.",
      "Inability to operate the platform owing to user-side issues (incorrect Excel uploads, missing columns, blocked seller portal access on the user's marketplace account, etc.).",
      "Decisions by the marketplace (Myntra, Nykaa, Ajio, etc.) to reject, throttle or de-list a seller's catalog after our AI has generated it.",
      "Downtime or service interruption attributable to our upstream LLM providers (Anthropic, Google, OpenAI) or hosting providers.",
      "Pricing changes — new lower prices launched after your purchase do not entitle you to a refund or top-up.",
    ],
  },
  {
    id: "credits-never-expire",
    title: "3. Credits never expire",
    body: [
      "To balance the strict non-refund policy, every listing credit you purchase NEVER expires. Use them whenever you want — next week, next quarter or next year.",
      "Credits are tied to the account email used at checkout and are non-transferable across organisations or seller accounts.",
    ],
  },
  {
    id: "wrong-payment",
    title: "4. Duplicate or wrong-amount payments",
    body: [
      "If you accidentally paid twice for the same plan, or transferred a higher amount than the listed plan price, write to business@brandborn.in within 48 hours of the transaction with proof of payment. We will validate the over-payment and either:",
    ],
    bullets: [
      "Convert the extra amount into additional listing credits at the same per-listing rate of the plan you purchased, or",
      "Apply the extra amount as advance balance against your next plan upgrade.",
    ],
  },
  {
    id: "chargebacks",
    title: "5. Chargebacks & disputes",
    body: [
      "Initiating a chargeback or payment dispute with your bank in violation of this policy will result in immediate suspension of your account, forfeiture of all credits, and may trigger legal recovery proceedings for any losses incurred by EcomCatalog AI.",
      "Before raising any external dispute, please first reach out to us via WhatsApp +91 94646 45491 or business@brandborn.in. We aim to resolve every legitimate concern within 3 business days.",
    ],
  },
  {
    id: "acceptance",
    title: "6. Acceptance",
    body: [
      "By scanning a QR, completing a UPI/bank transfer, or otherwise authorising payment to EcomCatalog AI, you confirm that you have read, understood and accepted this Refund Policy in full.",
    ],
  },
];
